import { FormControl } from '@angular/forms';
import { startWith, map } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  ModelSignal,
  OnDestroy,
  OnInit,
  output,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, NgForm } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatChipInput, MatChipInputEvent, MatChipsModule} from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { assetGroups } from '@constants/assets';
import {
  DashboardSidePanelData,
  WidgetCriteria,
  WidgetDisplayOption,
  WidgetDisplayOptionsByType,
  WidgetTimeframe,
  WidgetType,
  WidgetVisualization,
  YAxis,
} from '@constants/widgets';
import { deviceListQuery } from '@graphql/asset-management';
import { categorySelectOptionsQuery } from '@graphql/category';
import { getWidgetMetricTypesQuery } from '@graphql/dashboard';
import { groupSelectOptionsQuery } from '@graphql/group';
import { propertySelectOptionsQuery } from '@graphql/property';
import { TranslateService } from '@ngx-translate/core';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { blacklist, whitelist } from '@utils/widgets';
import { Apollo } from 'apollo-angular';
import {
  CategorySelectOptionsQuery,
  CategorySelectOptionsQueryVariables,
  ContractSelectOptionsQuery,
  ContractSelectOptionsQueryVariables,
  DeviceListQuery,
  DeviceListQueryVariables,
  GetWidgetMetricTypesQuery,
  GetWidgetMetricTypesQueryVariables,
  GroupSelectOptionsQuery,
  GroupSelectOptionsQueryVariables,
  PropertySelectOptionsQuery,
  PropertySelectOptionsQueryVariables,
  TariffSelectOptionsQuery,
  TariffSelectOptionsQueryVariables,
  UpdateWidgetMutationVariables,
} from 'gql/graphql';
import { Subscription } from 'rxjs';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  contractSelectOptionsQuery,
  tariffSelectOptionsQuery,
} from '@graphql/contracts-and-tariffs';
import { QuillModule } from 'ngx-quill';
import { UserSettingsService } from '@services/user/user-settings.service';
import { ToastService } from '@services/ui/toast.service';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';
import { LOADING } from '@utils/loader';
import Quill from 'quill';

@Component({
  selector: 'abb-dashboard-side-panel-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TPipe,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatChipsModule,
    AbbIconComponent,
    MatInputModule,
    MatButtonToggleModule,
    MatDatepickerModule,
    QuillModule,
    MatChipsModule,
    MatChipInput,
    NgxMatSelectSearchModule,
  ],
  template: `
    <form
      id="dashboard-side-pane-form"
      (ngSubmit)="onSubmit(form)"
      #form="ngForm"
    >
      <div class="flex flex-col flex-1 gap-y-4">
        @if (displayTitle()) {

        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Title' | t }}</mat-label>
            <input matInput [(ngModel)]="name" name="widget-name" [required]="isTitleRequired()" />
            <mat-error *ngIf="form.controls['widget-name']?.invalid">{{
              'This field is required' | t
            }}</mat-error>
          </mat-form-field>
        </div>
        } @if (displayCriteria()) {

        <div class="flex flex-col items-start mb-5">
          <mat-label>{{ 'Criteria' | t }}</mat-label>
          <mat-button-toggle-group
            [(ngModel)]="criteria"
            name="widget-criteria"
            [required]="isCriteriaRequired()"
          >
            @for (criteriaOption of criteriaOptions(); track
            criteriaOption.value) {
            <mat-button-toggle [value]="criteriaOption.value">{{
              criteriaOption.label | t
            }}</mat-button-toggle>
            }
          </mat-button-toggle-group>
        </div>
        } @if (displayPropertyOrGroup()) {
        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Property / Group' | t }}</mat-label>
            <mat-select
              name="property-group"
              [required]="displayPropertyOrGroup()"
              [(ngModel)]="selectedPropertyOrGroup"
            >
              <mat-option>
                <ngx-mat-select-search
                  placeholderLabel="{{ 'Search' | t }}"
                  [formControl]="propertyOrGroupFilterControl"
                  noEntriesFoundLabel="{{ 'No results found' | t }}"
                ></ngx-mat-select-search>
              </mat-option>
              @for (propertyOrGroup of filteredPropertiesOrGroups$ | async; track propertyOrGroup.name) {
                @if (propertyOrGroup.items && propertyOrGroup.items.length > 0) {
                  <mat-optgroup [label]="propertyOrGroup.name | t">
                    @for (option of propertyOrGroup.items; track option.id) {
                      <mat-option [value]="option">{{ option.name }}</mat-option>
                    }
                    </mat-optgroup> 
                }
              }
            </mat-select>
            <mat-error *ngIf="form.controls['property-group']?.invalid">
              {{ 'This field is required' | t }}
            </mat-error>
          </mat-form-field>
        </div>
        } @if (displayProperty()) {
        <ng-container
          *ngTemplateOutlet="
            singleAutocompleteTemplate;
            context: {
              $implicit: {
                options: properties,
                name: 'property',
                model: selectedProperty,
                label: ('Property' | t),
                required: isPropertyRequired()
              }
            }
          "
        ></ng-container>
        } @if (displayGroup()) {<ng-container
          *ngTemplateOutlet="
            singleAutocompleteTemplate;
            context: {
              $implicit: {
                options: groups,
                name: 'group',
                model: selectedGroup,
                label: ('Group' | t),
                required: isGroupRequired()
              }
            }
          "
        ></ng-container
        >} @if (displayProperties()) {
        <ng-container
          *ngTemplateOutlet="
            multiAutocompleteTemplate;
            context: {
              $implicit: {
                selectedItems: selectedProperties,
                disabled: false,
                name: 'properties',
                model: propertiesQuery,
                filteredItems: filteredProperties,
                label: ('Properties' | t),
                required: isPropertiesRequired()
              }
            }
          "
        ></ng-container
        >}@if (displayGroups()) {
        <ng-container
          *ngTemplateOutlet="
            multiAutocompleteTemplate;
            context: {
              $implicit: {
                selectedItems: selectedGroups,
                disabled: false,
                name: 'groups',
                model: groupsQuery,
                filteredItems: filteredGroups,
                label: ('Groups' | t),
                required: isGroupsRequired()
              }
            }
          "
        ></ng-container
        >} @if (displayContractOrTariffSelection()) {

        <div class="flex flex-col items-start mb-5">
          <mat-label>{{ 'Type' | t }}</mat-label>
          <mat-button-toggle-group
            [(ngModel)]="contractOrTariff"
            name="widget-contract-or-tariff"
            [required]="isContractOrTariffRequired()"
          >
            @for (contractOrTariffOption of contractOrTariffOptions(); track
            contractOrTariffOption.value) {
            <mat-button-toggle [value]="contractOrTariffOption.value">{{
              contractOrTariffOption.label | t
            }}</mat-button-toggle>
            }
          </mat-button-toggle-group>
          @if(form.controls['widget-contract-or-tariff'] && form.controls['widget-contract-or-tariff'].invalid){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </div>
        } @if (displayType()) {
        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Type' | t }}</mat-label>
            <mat-select
              [(ngModel)]="categoryId"
              name="category"
              [required]="isTypeRequired()"
              [panelClass]="'panel'"
            >
              @for (category of categories(); track category.id) {
              <mat-option [value]="category.id">{{
                category.name | t
              }}</mat-option>
              }
            </mat-select>
            @if(form.controls['category'] && form.controls['category'].invalid){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
        </div>
        } @if (displayDevices()) {
        <ng-container
          *ngTemplateOutlet="
            multiAutocompleteTemplate;
            context: {
              $implicit: {
                selectedItems: selectedDevices,
                disabled: displayType() && !categoryId(),
                name: 'devices',
                model: deviceQuery,
                filteredItems: filteredDevices,
                label: ('Devices' | t),
                required: isDevicesRequired()
              }
            }
          "
        ></ng-container>
        } @if (displayVisualizationMode()) {

        <div class="flex flex-col items-start mb-5">
          <mat-label>{{ 'Visualization' | t }}</mat-label>
          <mat-button-toggle-group
            [(ngModel)]="visualization"
            name="widget-visualization"
            [required]="isVisualizationRequired()"
          >
            @for (visualizationOption of visualizationOptions(); track
            visualizationOption.value) {
            <mat-button-toggle [value]="visualizationOption.value">{{
              visualizationOption.label | t
            }}</mat-button-toggle>
            }
          </mat-button-toggle-group>
          @if(form.controls['widget-visualization'] && form.controls['widget-visualization'].invalid){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </div>
        } @if (displayContract()) {
        <ng-container
          *ngTemplateOutlet="
            singleAutocompleteTemplate;
            context: {
              $implicit: {
                options: contracts,
                name: 'contract',
                model: selectedContract,
                label: ('Contract' | t),
                required: isContractRequired()
              }
            }
          "
        ></ng-container>
        }@if (displayTariff()) {
        <ng-container
          *ngTemplateOutlet="
            singleAutocompleteTemplate;
            context: {
              $implicit: {
                options: tariffs,
                name: 'tariff',
                model: selectedTariff,
                label: ('Tariff' | t),
                required: isTariffRequired()
              }
            }
          "
        ></ng-container>
        }@if (displayDevice()) {
          
        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Device' | t }}</mat-label>
            <mat-select
              name="device"
              [required]="isDeviceRequired()"
              [(ngModel)]="selectedDevice"
            >
              <mat-option>
                <ngx-mat-select-search
                  placeholderLabel="{{ 'Search' | t }}"
                  [formControl]="deviceSingleFilterControl"
                  noEntriesFoundLabel="{{ 'No results found' | t }}"
                ></ngx-mat-select-search>
              </mat-option>
              <mat-option *ngFor="let option of filteredDeviceOptions$ | async; trackBy: optionTrackBy" [value]="option">
                {{ option.name }}
              </mat-option>
            </mat-select>
            <mat-error *ngIf="form.controls['device']?.invalid">
              {{ 'This field is required' | t }}
            </mat-error>
          </mat-form-field>
        </div>
        } @if (displayMetric()) {

        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Metric' | t }}</mat-label>
            <mat-select
              [(ngModel)]="metricType"
              name="metric"
              [required]="isMetricRequired()"
              [panelClass]="'panel'"
            >
              @for (metricTypeOption of metricTypeOptions(); track
              metricTypeOption.id) {
              <mat-option [value]="metricTypeOption.id">{{
                metricTypeOption.name | t
              }}</mat-option>
              }
            </mat-select>
            @if(form.controls['metric'] && form.controls['metric'].invalid){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
        </div>
        } @if (displayOperations()) {
        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Operation' | t }}</mat-label>
            <mat-select
              [(ngModel)]="operation"
              name="operation"
              [panelClass]="'panel'"
              [required]="isOperationRequired()"
            >
              @for (operationOptions of operationOptions(); track
              operationOptions.value) {
              <mat-option [value]="operationOptions.value">{{
                operationOptions.label | t
              }}</mat-option>
              }
            </mat-select>
            @if(form.controls['operation'] && form.controls['operation'].invalid){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
        </div>
        } @if (displayConstantOrMetricToggle()) {

        <div class="flex flex-col items-start mb-5">
          <mat-label>{{ 'Against' | t }}</mat-label>
          <mat-button-toggle-group
            [(ngModel)]="constantOrMetric"
            name="widget-constant-or-metric"
            [required]="isConstantOrMetricToggleRequired()"
          >
            @for (constantOrOtherMetricOption of constantOrOtherMetricOptions();
            track constantOrOtherMetricOption.value) {
            <mat-button-toggle [value]="constantOrOtherMetricOption.value">{{
              constantOrOtherMetricOption.label | t
            }}</mat-button-toggle>
            }
          </mat-button-toggle-group>
        </div>
        } @if (displayConstant()) {
        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Constant' | t }}</mat-label>
            <input
              matInput
              [(ngModel)]="constant"
              type="number"
              name="constant"
              [required]="isConstantRequired()"
            />
            @if(form.controls['constant'] && form.controls['constant'].invalid){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
        </div>
        } @if (displayOtherMetricId()) {
        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Other metric' | t }}</mat-label>
            <mat-select
              [(ngModel)]="otherMetricId"
              name="other-metric"
              [required]="isOtherMetricRequired()"
              [panelClass]="'panel'"
            >
              @for (metricTypeOption of metricTypeOptions(); track
              metricTypeOption.id) {
              <mat-option [value]="metricTypeOption.id">{{
                metricTypeOption.name | t
              }}</mat-option>
              }
            </mat-select>
            @if(form.controls['other-metric'] && form.controls['other-metric'].invalid){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
        </div>
        } @if (displayTimeframe()) {
        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Timeframe' | t }}</mat-label>
            <mat-select
              [(ngModel)]="timeframe"
              name="timeframe"
              [required]="isTimeframeRequired()"
              [panelClass]="'panel'"
            >
              @for (timeframeOption of timeframeOptions(); track
              timeframeOption.value) {
              <mat-option [value]="timeframeOption.value">{{
                timeframeOption.name | t
              }}</mat-option>
              }
            </mat-select>
            @if(form.controls['timeframe'] && form.controls['timeframe'].invalid){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
        </div>
        } @if (displayTimeframeDate()) {

        <mat-form-field subscriptSizing="dynamic">
          <mat-label>{{ 'Date' | t }}</mat-label>
          <input
            matInput
            [matDatepicker]="timeframeDatePicker"
            [(ngModel)]="timeframeDate"
            (dateChange)="onDateChange($event.value)"
            name="timeframe-date"
          />
          <mat-datepicker-toggle
            matIconSuffix
            [for]="timeframeDatePicker"
          ></mat-datepicker-toggle>
          <mat-datepicker #timeframeDatePicker [panelClass]="'panel'">
          </mat-datepicker>
        </mat-form-field>
        } @if (displayColumns()) {
        <ng-container
          *ngTemplateOutlet="
            multiAutocompleteTemplate;
            context: {
              $implicit: {
                selectedItems: selectedColumns,
                disabled: false,
                name: 'columns',
                model: columnsQuery,
                filteredItems: filteredColumns,
                label: ('Columns' | t),
                required: isColumnsRequired()
              }
            }
          "
        ></ng-container>
        } @if (displayDisplay()) {

        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Display' | t }}</mat-label>
            <mat-select
              [(ngModel)]="display"
              name="display"
              [required]="isDisplayRequired()"
              [panelClass]="'panel'"
            >
              @for (displayOption of displayOptions(); track displayOption.id) {
              <mat-option [value]="displayOption.id">{{
                displayOption.name | t
              }}</mat-option>
              }
            </mat-select>
            @if(form.controls['display'] && form.controls['display'].invalid){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
        </div>
        } @if (displayHasYAxis()) {
        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Y axis' | t }}</mat-label>
            <mat-select
              [required]="
                displayHasYAxis() &&
                display() === WidgetDisplayOption.CHART_LINEAR
              "
              [(ngModel)]="yAxis"
              name="yAxis"
              [panelClass]="'panel'"
            >
              @for (yAxisOption of yAxisOptions; track yAxisOption.id) {
              <mat-option [value]="yAxisOption.id">{{
                yAxisOption.name
              }}</mat-option>
              }
            </mat-select>
            <mat-error *ngIf="form.controls['yAxis']?.invalid">{{
              'This field is required' | t
            }}</mat-error>
          </mat-form-field>
        </div>
        } @if (displayMinMax()) {
        <div class="flex gap-x-4">
          <mat-form-field class="flex-1">
            <mat-label>{{ 'Min' | t }}</mat-label>
            <input matInput [(ngModel)]="min" type="number" name="min" />
          </mat-form-field>
          <mat-form-field class="flex-1">
            <mat-label>{{ 'Max' | t }}</mat-label>
            <input matInput [(ngModel)]="max" type="number" name="max" [min]="min() ?? null" />
          </mat-form-field>
        </div>
        } @if (displayTargetValues()) {
        <div class="flex gap-x-4">
          <mat-form-field class="flex-1">
            <mat-label>{{ 'Target (min)' | t }}</mat-label>
            <input
              matInput
              [(ngModel)]="minTarget"
              type="number"
              name="minTarget"
            />
          </mat-form-field>
          <mat-form-field class="flex-1">
            <mat-label>{{ 'Target (max)' | t }}</mat-label>
            <input
              matInput
              [(ngModel)]="maxTarget"
              type="number"
              [min]="minTarget() ?? null"
              name="maxTarget"
            />
          </mat-form-field>
        </div>
        } @if(displayTargetList()) {
        <div class="flex flex-col gap-y-2">
          <p>{{ 'Targets' | t }}</p>
          @if (targetListElements().length === 0) {
          <p class="text-sm text-abb-font/60">
            {{ 'Select at least one property' | t }}
          </p>
          } @for (el of targetListElements(); track el.id) {
          <mat-form-field>
            <mat-label>{{ el.name }}</mat-label>
            <input
              matInput
              [(ngModel)]="targets()[el.id]"
              type="number"
              [name]="el.name + 'target'"
            />
          </mat-form-field>
          }
        </div>
        } @if (displayConversionFactor()) {
        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ 'Conversion factor' | t }}</mat-label>
            <input
              matInput
              [(ngModel)]="conversionFactor"
              type="number"
              name="conversionFactor"
            />
          </mat-form-field>
        </div>
        } @if (displayTextEditor()) {
        <div class="max-w-lg">
          <quill-editor
            name="rich-text-editor"
            [(ngModel)]="richTextEditorContent"
            (onEditorCreated)="onEditorCreated($event)"
            [format]="'html'"
          ></quill-editor>
        </div>
        } @if (displayMetrics()) {
        <ng-container
          *ngTemplateOutlet="
            multiAutocompleteTemplate;
            context: {
              $implicit: {
                selectedItems: selectedMetrics,
                disabled: false,
                name: 'metrics',
                model: metricsQuery,
                filteredItems: filteredMetrics,
                label: ('Metrics' | t),
                required: isMetricsRequired()
              }
            }
          "
        ></ng-container>
        }@if(displayTargetsWithThresholds()) {
        <div class="grid grid-cols-2 gap-2">
          <p>{{ 'Metrics Target' | t }}</p>
          <p>{{ 'Metrics Threshold' | t }}</p>
          @if (targetListElements().length === 0) {
          <p class="text-sm text-abb-font/60">
            {{ 'Select at least one metric' | t }}
          </p>
          } @for (el of targetListElements(); track el.id) {
          <mat-form-field>
            <mat-label>{{ el.name | t }}</mat-label>
            <input
              matInput
              [(ngModel)]="targets()[el.id]"
              type="number"
              [name]="el.name + 'target'"
            />
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ el.name | t }}</mat-label>
            <mat-select
              [name]="el.name + 'threshold'"
              [(ngModel)]="thresholds()[el.id]"
              [panelClass]="'panel'"
            >
              <mat-option [value]="''">{{ 'N/A' | t }}</mat-option>
              <mat-option [value]="'>'">{{
                'If Metric greater than Metric Target is Good' | t
              }}</mat-option>
              <mat-option [value]="'<'">{{
                'If Metric lower than Metric Target is Good' | t
              }}</mat-option>
            </mat-select>
          </mat-form-field>
          }
        </div>
        }
      </div>

      <ng-template #multiAutocompleteTemplate let-item>
        <div>
          <mat-form-field class="w-full">
            <mat-label>{{ item.label }}*</mat-label>
            <mat-chip-grid #chipList style="padding-top: 0.7em;">
              <mat-chip
                *ngFor="let el of item.selectedItems()"
                [removable]="true"
                (removed)="removeItem(item.selectedItems, el)"
                class="chips"
              >
                {{ el.name | t }}
                <button matChipRemove type="button">
                  <abb-icon size="small" name="mat-outlined/close"></abb-icon>
                </button>
              </mat-chip>
              <input
                type="text"
                [name]="item.name"
                matInput
                [matAutocomplete]="auto"
                [matChipInputFor]="chipList"
                [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                [required]="item.required && item.selectedItems().length === 0"
                [(ngModel)]="item.model"
                (focus)="item.model = ''"
                (keydown)="preventTyping($event)"
                [disabled]="
                  item.disabled ||
                  (this.widgetType() === WidgetType.ENERGY_CONSUMPTION &&
                    item.selectedItems().length >= 10)
                "
              />
            </mat-chip-grid>
            <mat-autocomplete class="panel" #auto="matAutocomplete" (optionSelected)="onSelect($event, item.selectedItems, item.model)">
              <mat-option *ngFor="let el of item.filteredItems()" [value]="el">
                {{ el.name | t }}
              </mat-option>
            </mat-autocomplete>
            <input 
              type="hidden" 
              [name]="item.name + '-ids'" 
              [ngModel]="getSelectedItemsValue(item.selectedItems())"
            />
            <mat-hint *ngIf="this.widgetType() === WidgetType.ENERGY_CONSUMPTION && item.selectedItems().length >= 10" class="text-sm text-orange-600 mt-1">
              {{ 'Maximum 10 devices can be selected' | t }}
            </mat-hint>
          </mat-form-field>
        </div>
      </ng-template>

      <ng-template #singleAutocompleteTemplate let-item>
        <div>
          <input 
            type="hidden" 
            [name]="item.name" 
            [value]="getSelectedItemValue(item.model())"
            [required]="item.required"
          />
          <mat-form-field class="w-full">
            <mat-label>{{ item.label }}</mat-label>
            <mat-select
              [name]="item.name + '-display'"
              [required]="item.required"
              [(ngModel)]="item.model"
            >   @for (option of item.options(); track option.id) {

                <ng-container>
                  <mat-option [value]="option">{{ option.name }}</mat-option>
                </ng-container>
              
              }
            </mat-select>
            @if (item.required && !item.model) {
              <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
        </div>
          </ng-template>
    </form>
  `,
  styles: ``,
  host: {
    class: 'flex flex-col flex-1',
  },
})
export class DashboardSidePanelFormComponent implements OnDestroy, OnInit {
  private forcePropertyOrGroupListRefresh() {
    setTimeout(() => {
      this.propertyOrGroupFilterControl.setValue(this.propertyOrGroupFilterControl.value ?? '');
    }, 0);
  }


  timezone = signal<string>('');

  readonly WidgetType = WidgetType;
  readonly assetGroups = assetGroups;
  readonly WidgetDisplayOption = WidgetDisplayOption;

  widgetType = input.required<WidgetType>();
  widgetData = input<DashboardSidePanelData['widget']>();
  mode = input.required<'new' | 'edit' | 'clone'>();
  cancel = output();
  abbSubmit = output<{
    sharedVariables: Omit<UpdateWidgetMutationVariables, 'id' | 'dashboardId'>;
    display: WidgetDisplayOption;
  }>();

  metricTypeOptions = signal<GetWidgetMetricTypesQuery['getWidgetMetricTypes']>(
    []
  );
  metricType = model<string>();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  deviceQuery = model<string | undefined>('');
  name = model<string>();
  criteria = model<WidgetCriteria>();
  criteriaOptions = computed(() => [
    ...(whitelist(
      this.widgetType(),
      WidgetType.COST_BENCHMARK,
      WidgetType.MAX_MIN_VALUES,
      WidgetType.ENERGY_CONSUMPTION,
      WidgetType.WATER_GAS,
      WidgetType.ACTUAL_VS_TARGET_VALUES,
      WidgetType.KPI_CALCULATION
    )
      ? [{ value: WidgetCriteria.DEVICE, label: 'Device' }]
      : []),
    { value: WidgetCriteria.GROUP, label: 'Group' },
    { value: WidgetCriteria.PROPERTY, label: 'Property' },
  ]);
  operation = model<string>();
  operationOptions = computed(() => [
    { value: '+', label: 'Add (+)' },
    { value: '-', label: 'Subtract (-)' },
    { value: '*', label: 'Multiply (*)' },
    { value: '/', label: 'Divide (/)' },
  ]);
  visualization = model<WidgetVisualization>();
  visualizationOptions = computed(() => [
    { value: WidgetVisualization.AVERAGE, label: 'Average' },
    { value: WidgetVisualization.SINGLE, label: 'Single' },
  ]);
  display = model<WidgetDisplayOption>();
  displayOptions = computed(
    () => WidgetDisplayOptionsByType[this.widgetType()]
  );
  yAxis = model<YAxis>();
  yAxisOptions = [
    { id: YAxis.AUTOSCALE, name: 'Autoscale' },
    { id: YAxis.MANUAL, name: 'Manual' },
  ];

  contractOrTariff = model<'contract' | 'tariff' | null>();
  contractOrTariffOptions = computed(() => [
    { value: 'contract', label: 'Contract' },
    { value: 'tariff', label: 'Tariff' },
  ]);
  constantOrOtherMetricOptions = computed(() => [
    { value: 'constant', label: 'Constant' },
    { value: 'metric', label: 'Other metric' },
  ]);
  min = model<number>();
  max = model<number>();
  minTarget = model<number>();
  maxTarget = model<number>();
  conversionFactor = model<number>();
  constant = model<number>();
  otherMetricId = model<string>();
  constantOrMetric = model<'constant' | 'metric'>();

  selectedPropertyOrGroup = model<
    | {
        id: string;
        name: string;
        __typename?: string;
      }
    | string
    | null
  >(null);

  selectedProperty = model<
    | {
        id: string;
        name: string;
        __typename?: string;
      }
    | string
    | null
  >(null);
  selectedDevice = model<
    | {
        id: string;
        name: string;
        __typename?: string;
      }
    | string
    | null
  >(null);
  selectedGroup = model<
    | {
        id: string;
        name: string;
        properties: {
          id: string;
        }[];
        __typename?: string;
      }
    | string
    | null
  >(null);
  selectedTariff = model<
    | {
        id: string;
        name: string;
        __typename?: string;
      }
    | string
    | null
  >(null);
  selectedContract = model<
    | {
        id: string;
        name: string;
        __typename?: string;
      }
    | string
    | null
  >(null);

  tariffs = signal<
    {
      __typename?: 'Tariff';
      id: string;
      name: string;
    }[]
  >([]);
  contracts = signal<ContractSelectOptionsQuery['contractList']>([]);
  
  allContracts = signal<ContractSelectOptionsQuery['contractList']>([]);

  selectedDevices = signal<DeviceListQuery['deviceList']>([]);

  propertiesQuery = model<string | undefined>('');
  selectedProperties = signal<{ id: string; name: string }[]>([]);

  groupsQuery = model<string | undefined>('');
  selectedGroups = signal<{ id: string; name: string }[]>([]);

  columnsQuery = model<string | undefined>('');
  selectedColumns = signal<{ id: string; name: string }[]>([]);

  metricsQuery = model<string | undefined>('');
  selectedMetrics = signal<{ id: string; name: string }[]>([]);

  columns = computed(() => {
    if (this.widgetType() === WidgetType.ALARM_LOG) {
      return [
        {
          id: 'name',
          name: t('dashboards.alarm_log', 'Name', this.translate),
        },
        {
          id: 'state',
          name: t('dashboards.alarm_log', 'State', this.translate),
        },
        {
          id: 'condition',
          name: t('dashboards.alarm_log', 'Condition', this.translate),
        },
        {
          id: 'time',
          name: t('dashboards.alarm_log', 'Time', this.translate),
        },
      ];
    }
    if (this.widgetType() === WidgetType.AUTOMATIONS_LOG) {
      return [
        {
          id: 'name',
          name: t('dashboards.automations_log', 'Name', this.translate),
        },
        {
          id: 'conditions',
          name: t(
            'dashboards.automations_log',
            'Conditions (WHEN/IF/THEN)',
            this.translate
          ),
        },
        {
          id: 'time',
          name: t('dashboards.automations_log', 'Time', this.translate),
        },
        {
          id: 'property',
          name: t('dashboards.automations_log', 'Property', this.translate),
        },
        {
          id: 'groups',
          name: t('dashboards.automations_log', 'Groups', this.translate),
        },
      ];
    }
    if (this.widgetType() === WidgetType.ONLINE_EVENTS) {
      return [
        {
          id: 'name',
          name: t('dashboards.online_events', 'Name', this.translate),
        },
        {
          id: 'device',
          name: t('dashboards.online_events', 'Device', this.translate),
        },
        {
          id: 'category',
          name: t('dashboards.online_events', 'Category', this.translate),
        },
        {
          id: 'status',
          name: t('dashboards.online_events', 'Status', this.translate),
        },
        {
          id: 'dateAndTime',
          name: t('dashboards.online_events', 'Date and Time', this.translate),
        },
      ];
    }
    return [];
  });

  filteredColumns = computed(() => {
    const columnsQuery = this.columnsQuery()?.toLowerCase() ?? '';
    return this.columns().filter(
      (c) =>
        c.name.toLowerCase().includes(columnsQuery) &&
        !this.selectedColumns().some((sel) => sel.id === c.id)
    );
  });

  categoryId = model<string | null>(null);

  timeframe = model<WidgetTimeframe | null>(null);
  
  timeframeOptions = computed(() => {
    const baseOptions = [
      { value: WidgetTimeframe.TODAY, name: 'Today' },
      { value: WidgetTimeframe.YESTERDAY, name: 'Yesterday' },
      { value: WidgetTimeframe.LAST_WEEK, name: 'Last week' },
      { value: WidgetTimeframe.LAST_MONTH, name: 'Last month' },
      { value: WidgetTimeframe.LAST_YEAR, name: 'Last year' },
      { value: WidgetTimeframe.SPECIFIC_DATE, name: 'Specific date' },
    ];
    return this.widgetType() === WidgetType.ONLINE_EVENTS
      ? baseOptions.filter((opt) => opt.value !== WidgetTimeframe.LAST_YEAR)
      : baseOptions;
  });
  timeframeDate = model<Date | null>(null);

  targets = model<Record<string, number>>({});
  thresholds = model<Record<string, string>>({});

  richTextEditorContent = model<string>();

  targetListElements = computed(() => {
    if (
      this.widgetType() === WidgetType.CO2_EMISSIONS &&
      this.visualization() === WidgetVisualization.SINGLE
    ) {
      return this.selectedProperties();
    } else if (this.widgetType() === WidgetType.CO2_EMISSIONS) {
      return [{ id: 'all', name: t(this.prefix, `Average`, this.translate) }];
    } else if (this.widgetType() === WidgetType.ACTUAL_VS_TARGET_VALUES) {
      return this.selectedMetrics();
    }
    return [];
  });

  propertiesOrGroups = computed(() => [
    {
      name: 'Properties',
      items: this.properties(),
    },
    {
      name: 'Groups',
      items: this.groups(),
    },
  ]);

  groups = signal<GroupSelectOptionsQuery['groupList']>([]);
  properties = signal<PropertySelectOptionsQuery['propertyList']>([]);
  categories = signal<CategorySelectOptionsQuery['categoryList']>([]);
  devices = signal<DeviceListQuery['deviceList']>([]);
  filteredPropertiesOrGroups = computed(() => {
    const filterValue = (this.propertyOrGroupFilterControl.value ?? '').toLowerCase();
    if (!filterValue) {
      return this.propertiesOrGroups();
    }
    return this.propertiesOrGroups().map(group => ({
      ...group,
      items: group.items.filter((item: any) =>
        item.name.toLowerCase().includes(filterValue)
      )
    })).filter(group => group.items.length > 0);
  });
  filteredDevicesByType = computed(() => {
    return this.devices().filter(
      (d) => !this.displayType() || this.categoryId() === d.category.id
    );
  });

  filteredDevices = computed(() => {
    const deviceQuery = this.deviceQuery()?.toLowerCase() ?? '';
    const selectedPropertyOrGroup = this.selectedPropertyOrGroup();
    return this.devices().filter((d) => {
      const matchesQuery = d.name.toLowerCase().includes(deviceQuery);
      const isNotSelected = !this.selectedDevices().some((sel) => sel.id === d.id);
      const shouldFilterByCategory = this.displayType() && this.categoryId();
      const matchesCategory = shouldFilterByCategory
        ? d.category.id === this.categoryId()
        : true;

      let matchesPropertyOrGroup = true;
      if (selectedPropertyOrGroup && typeof selectedPropertyOrGroup === 'object' && selectedPropertyOrGroup.id) {
        if ('properties' in selectedPropertyOrGroup && Array.isArray(selectedPropertyOrGroup.properties)) {
          matchesPropertyOrGroup = d.groups && d.groups.some((g: any) => g.id === selectedPropertyOrGroup.id);
        } else {
          matchesPropertyOrGroup = d.property && d.property.id === selectedPropertyOrGroup.id;
        }
      }

      return (
        matchesPropertyOrGroup &&
        matchesQuery &&
        isNotSelected &&
        matchesCategory
      );
    });
  });
  filteredDevicesBase$ = new BehaviorSubject<any[]>([]);
  filteredProperties = computed(() => {
    const propertyQuery = this.propertiesQuery()?.toLowerCase() ?? '';
    return this.properties().filter(
      (d) =>
        d.name.toLowerCase().includes(propertyQuery) &&
        !this.selectedProperties().some((sel) => sel.id === d.id)
    );
  });
  filteredGroups = computed(() => {
    const groupQuery = this.groupsQuery()?.toLowerCase() ?? '';
    return this.groups().filter(
      (d) =>
        d.name.toLowerCase().includes(groupQuery) &&
        !this.selectedGroups().some((sel) => sel.id === d.id)
    );
  });
  filteredMetrics = computed(() => {
    const metricsQuery = this.metricsQuery()?.toLowerCase() ?? '';
    return this.metricTypeOptions().filter(
      (m) =>
        m.name.toLowerCase().includes(metricsQuery) &&
        !this.selectedMetrics().some((sel) => sel.id === m.id)
    );
  });

  displayHasYAxis = computed(
    () => this.display() === WidgetDisplayOption.CHART_LINEAR
  );

  displayTitle = computed(() => blacklist(this.widgetType()));

  displayPropertyOrGroup = computed(
    () =>
      whitelist(
        this.widgetType(),
        WidgetType.CUSTOM_VALUES,
        WidgetType.ELECTRICAL_VALUES,
        WidgetType.ENERGY_VALUES,
      ) ||
      (whitelist(
        this.widgetType(),
        WidgetType.MAX_MIN_VALUES,
        WidgetType.WATER_GAS,
        WidgetType.COST_BENCHMARK
      ) &&
        this.criteria() === WidgetCriteria.DEVICE)
  );
  displayCriteria = computed(() =>
    whitelist(
      this.widgetType(),
      WidgetType.COST_BENCHMARK,
      WidgetType.ENERGY_COST_BREAKDOWN,
      WidgetType.MAX_MIN_VALUES,
      WidgetType.ENERGY_CONSUMPTION,
      WidgetType.WATER_GAS,
      WidgetType.CONTRACTS_AND_TARIFFS,
      WidgetType.ACTUAL_VS_TARGET_VALUES,
      WidgetType.KPI_CALCULATION
    )
  );
  displayType = computed(
    () =>
      whitelist(
        this.widgetType(),
        WidgetType.CUSTOM_VALUES,
        WidgetType.ELECTRICAL_VALUES,
        WidgetType.ENERGY_VALUES,
        WidgetType.POWER_QUALITY
      ) ||
      (whitelist(
        this.widgetType(),
        WidgetType.MAX_MIN_VALUES,
        WidgetType.WATER_GAS,
        WidgetType.KPI_CALCULATION,
        WidgetType.COST_BENCHMARK
      ) &&
        this.criteria() === WidgetCriteria.DEVICE) ||
      (whitelist(this.widgetType(), WidgetType.ENERGY_CONSUMPTION) &&
        this.criteria() === WidgetCriteria.DEVICE)
  );
  displayDisplay = computed(() => this.displayOptions().length > 1);
  displayMetric = computed(
    () =>
      (whitelist(
        this.widgetType(),
        WidgetType.CUSTOM_VALUES,
        WidgetType.ELECTRICAL_VALUES,
        WidgetType.ENERGY_VALUES,
        WidgetType.POWER_QUALITY,
        WidgetType.ENERGY_CONSUMPTION,
        WidgetType.KPI_CALCULATION,
        WidgetType.WATER_GAS
      ) ||
        (whitelist(this.widgetType(), WidgetType.MAX_MIN_VALUES) &&
          this.criteria() === WidgetCriteria.DEVICE)) &&
      (this.display() !== WidgetDisplayOption.TABLE ||
        this.widgetType() === WidgetType.ENERGY_CONSUMPTION)
  );
  displayMetrics = computed(() =>
    whitelist(this.widgetType(), WidgetType.ACTUAL_VS_TARGET_VALUES)
  );
  displayProperties = computed(
    () =>
      whitelist(
        this.widgetType(),
        WidgetType.N_OF_ALARMS,
        WidgetType.ALARM_LOG,
        WidgetType.AUTOMATIONS_LOG,
        WidgetType.DEVICE_STATUS,
        WidgetType.ONLINE_EVENTS,
        WidgetType.CO2_EMISSIONS,
        WidgetType.LOCATION
      ) ||
      (whitelist(
        this.widgetType(),
        WidgetType.COST_BENCHMARK,
        WidgetType.ENERGY_CONSUMPTION
      ) &&
        this.criteria() === WidgetCriteria.PROPERTY)
  );
  displayProperty = computed(
    () =>
      (whitelist(
        this.widgetType(),
        WidgetType.ENERGY_COST_BREAKDOWN,
        WidgetType.MAX_MIN_VALUES,
        WidgetType.WATER_GAS,
        WidgetType.KPI_CALCULATION,
        WidgetType.ACTUAL_VS_TARGET_VALUES,
        WidgetType.CONTRACTS_AND_TARIFFS,
      ) && this.criteria() === WidgetCriteria.PROPERTY) ||
       whitelist(
        this.widgetType(),
        WidgetType.POWER_QUALITY
      )
  );
  displayGroups = computed(
    () =>
      whitelist(
        this.widgetType(),
        WidgetType.COST_BENCHMARK,
        WidgetType.ENERGY_CONSUMPTION
      ) && this.criteria() === WidgetCriteria.GROUP
  );
  displayGroup = computed(
    () =>
      whitelist(
        this.widgetType(),
        WidgetType.ENERGY_COST_BREAKDOWN,
        WidgetType.MAX_MIN_VALUES,
        WidgetType.WATER_GAS,
        WidgetType.KPI_CALCULATION,
        WidgetType.ACTUAL_VS_TARGET_VALUES,
        WidgetType.CONTRACTS_AND_TARIFFS
      ) && this.criteria() === WidgetCriteria.GROUP
  );
  displayDevices = computed(
    () =>
      whitelist(
        this.widgetType(),
        WidgetType.CUSTOM_VALUES,
        WidgetType.POWER_QUALITY
      ) ||
      (whitelist(
        this.widgetType(),
        WidgetType.COST_BENCHMARK,
        WidgetType.ENERGY_CONSUMPTION
      ) &&
        this.criteria() === WidgetCriteria.DEVICE) ||
      (whitelist(this.widgetType(), WidgetType.ELECTRICAL_VALUES) &&
        this.display() !== WidgetDisplayOption.GAUGE) ||
      (whitelist(this.widgetType(), WidgetType.ENERGY_VALUES) &&
        this.display() !== WidgetDisplayOption.GAUGE &&
        ((this.display() === WidgetDisplayOption.CHART_DONUT &&
          this.categoryId() === '2') ||
          this.display() !== WidgetDisplayOption.CHART_DONUT))
  );

   displayDevice = computed(
    () =>
      (whitelist(
        this.widgetType(),
        WidgetType.MAX_MIN_VALUES,
        WidgetType.WATER_GAS,
        WidgetType.KPI_CALCULATION,
        WidgetType.ACTUAL_VS_TARGET_VALUES
      ) &&
        this.criteria() === WidgetCriteria.DEVICE) ||
      (whitelist(this.widgetType(), WidgetType.ELECTRICAL_VALUES) &&
        this.display() === WidgetDisplayOption.GAUGE) ||
      (whitelist(this.widgetType(), WidgetType.ENERGY_VALUES) &&
        (this.display() === WidgetDisplayOption.GAUGE ||
          (this.display() === WidgetDisplayOption.CHART_DONUT &&
            this.categoryId() !== '2')))
  );

  displayTimeframe = computed(() =>
    whitelist(
      this.widgetType(),
      WidgetType.COST_BENCHMARK,
      WidgetType.ENERGY_COST_BREAKDOWN,
      WidgetType.MAX_MIN_VALUES,
      WidgetType.ENERGY_CONSUMPTION,
      WidgetType.WATER_GAS,
      WidgetType.CONTRACTS_AND_TARIFFS,
      WidgetType.N_OF_ALARMS,
      WidgetType.ALARM_LOG,
      WidgetType.AUTOMATIONS_LOG,
      WidgetType.ONLINE_EVENTS,
      WidgetType.ACTUAL_VS_TARGET_VALUES
    )
  );
  displayTimeframeDate = computed(
    () => this.timeframe() === WidgetTimeframe.SPECIFIC_DATE
  );

  displayOperations = computed(() =>
    whitelist(this.widgetType(), WidgetType.KPI_CALCULATION)
  );
  displayColumns = computed(() =>
    whitelist(
      this.widgetType(),
      WidgetType.ALARM_LOG,
      WidgetType.AUTOMATIONS_LOG,
      WidgetType.ONLINE_EVENTS
    )
  );
  displayMinMax = computed(
    () =>
      this.display() === WidgetDisplayOption.GAUGE ||
      (this.display() === WidgetDisplayOption.CHART_LINEAR &&
        this.yAxis() === YAxis.MANUAL)
  );

  displayTargetValues = computed(
    () => this.display() === WidgetDisplayOption.GAUGE
  );
  displayTargetList = computed(() =>
    whitelist(this.widgetType(), WidgetType.CO2_EMISSIONS)
  );

  displayContractOrTariffSelection = computed(() =>
    whitelist(this.widgetType(), WidgetType.CONTRACTS_AND_TARIFFS)
  );

  displayTariff = computed(
    () =>
      this.displayContractOrTariffSelection() &&
      this.contractOrTariff() === 'tariff'
  );
  displayContract = computed(
    () =>
      this.displayContractOrTariffSelection() &&
      this.contractOrTariff() === 'contract'
  );
  displayVisualizationMode = computed(() =>
    whitelist(this.widgetType(), WidgetType.CO2_EMISSIONS)
  );
  displayConversionFactor = computed(() =>
    whitelist(this.widgetType(), WidgetType.CO2_EMISSIONS)
  );
  displayTextEditor = computed(() =>
    whitelist(this.widgetType(), WidgetType.TEXT)
  );

  displayTargetsWithThresholds = computed(() =>
    whitelist(this.widgetType(), WidgetType.ACTUAL_VS_TARGET_VALUES)
  );

  displayConstantOrMetricToggle = computed(() =>
    whitelist(this.widgetType(), WidgetType.KPI_CALCULATION)
  );
  displayConstant = computed(
    () =>
      whitelist(this.widgetType(), WidgetType.KPI_CALCULATION) &&
      this.constantOrMetric() === 'constant'
  );
  displayOtherMetricId = computed(
    () =>
      whitelist(this.widgetType(), WidgetType.KPI_CALCULATION) &&
      this.constantOrMetric() === 'metric'
  );

  isTitleRequired = computed(() =>
    this.displayTitle()
  );

  isCriteriaRequired = computed(() =>
    this.displayCriteria()
  );

  isPropertyOrGroupRequired = computed(() =>
    this.displayPropertyOrGroup()
  );

  isTypeRequired = computed(() =>
    this.displayType() &&
    (!this.displayCriteria() || this.criteria() === WidgetCriteria.DEVICE)
  );

  isDeviceRequired = computed(() =>
    this.displayDevice() &&
    (!this.displayCriteria() || this.criteria() === WidgetCriteria.DEVICE)
  );

  isPropertyRequired = computed(() =>
    this.displayProperty() &&
    (!this.displayCriteria() || this.criteria() === WidgetCriteria.PROPERTY)
  );

  isGroupRequired = computed(() =>
    this.displayGroup() &&
    (!this.displayCriteria() || this.criteria() === WidgetCriteria.GROUP)
  );

  isDevicesRequired = computed(() =>
    this.displayDevices()
  );

  isPropertiesRequired = computed(() =>
    this.displayProperties()
  );

  isGroupsRequired = computed(() =>
    this.displayGroups()
  );

  isMetricRequired = computed(() =>
    this.displayMetric()
  );

  isMetricsRequired = computed(() =>
    this.displayMetrics()
  );

  isOperationRequired = computed(() =>
    this.displayOperations()
  );

  isConstantOrMetricToggleRequired = computed(() =>
    this.displayConstantOrMetricToggle()
  );

  isConstantRequired = computed(() =>
    this.displayConstant() && this.constantOrMetric() === 'constant'
  );

  isOtherMetricRequired = computed(() =>
    this.displayOtherMetricId() && this.constantOrMetric() === 'metric'
  );

  isTimeframeRequired = computed(() =>
    this.displayTimeframe()
  );

  isTimeframeDateRequired = computed(() =>
    this.displayTimeframeDate()
  );

  isDisplayRequired = computed(() =>
    this.displayDisplay()
  );

  isVisualizationRequired = computed(() =>
    this.displayVisualizationMode()
  );

  isContractOrTariffRequired = computed(() =>
    this.displayContractOrTariffSelection()
  );

  isContractRequired = computed(() =>
    this.displayContract()
  );

  isTariffRequired = computed(() =>
    this.displayTariff()
  );

  isColumnsRequired = computed(() =>
    this.displayColumns()
  );

  isConversionFactorRequired = computed(() =>
    this.displayConversionFactor()
  );

  isRichTextEditorRequired = computed(() =>
    this.displayTextEditor()
  );

  private readonly prefix = inject(TRANSLATION_PREFIX);
  private readonly subscriptions: Subscription[] = [];
  constructor(
    readonly userService: UserSettingsService,
    readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService
  ) {
    this.timezone.set(userService.timezone());
    effect(() => {
      this.filteredDevicesBase$.next(this.filteredDevicesByType());
    });
    let firstPropertyOrGroupChange = true;
    this.selectedPropertyOrGroup.subscribe(() => {
      if (firstPropertyOrGroupChange) {
        firstPropertyOrGroupChange = false;
        return;
      }
      this.selectedDevices.set([]);
      this.selectedDevice.set(null);
      this.filteredDevicesBase$.next(this.filteredDevices());
    });

    this.subscriptions.push(
      apollo
        .watchQuery<GroupSelectOptionsQuery, GroupSelectOptionsQueryVariables>({
          query: groupSelectOptionsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.groups.set(data.groupList);
            this.forcePropertyOrGroupListRefresh();
            const widgetData = this.widgetData();
            if (widgetData?.groupId) {
              const group =
                data.groupList.find((g) => g.id === widgetData.groupId) ?? null;
              if (this.displayPropertyOrGroup()) {
                this.selectedPropertyOrGroup.set(group);
              } else if (this.displayGroup()) {
                this.selectedGroup.set(group);
              }
            }
            if (widgetData?.groupIds) {
              this.selectedGroups.set(
                data.groupList.filter((g) =>
                  widgetData.groupIds?.includes(g.id)
                )
              );
            }
          },
          error: (err) => {
          },
        }),
      apollo
        .watchQuery<
          PropertySelectOptionsQuery,
          PropertySelectOptionsQueryVariables
        >({
          query: propertySelectOptionsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.properties.set(data.propertyList);
            this.forcePropertyOrGroupListRefresh();
            const widgetData = this.widgetData();
            if (widgetData?.propertyId) {
              const property =
                data.propertyList.find((p) => p.id === widgetData.propertyId) ??
                null;
              if (this.displayPropertyOrGroup()) {
                this.selectedPropertyOrGroup.set(property);
              } else if (this.displayProperty()) {
                this.selectedProperty.set(property);
              }
            }
            if (widgetData?.propertyIds) {
              this.selectedProperties.set(
                data.propertyList.filter((p) =>
                  widgetData.propertyIds?.includes(p.id)
                )
              );
            }
          },
          error: (err) => {
          },
        }),
      apollo
        .watchQuery<
          ContractSelectOptionsQuery,
          ContractSelectOptionsQueryVariables
        >({
          query: contractSelectOptionsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.allContracts.set(data.contractList);
            this.contracts.set(data.contractList);
            const allTariffs =
              data.contractList.flatMap((c) => c.tariffs) ?? [];
            this.tariffs.set(allTariffs);
            const widgetData = this.widgetData();
            if (widgetData?.contractId) {
              this.selectedContract.set(
                data.contractList.find((c) => c.id === widgetData.contractId) ??
                  null
              );
            }
          },
          error: (err) => {
          },
        })
    );
    this.categoryId.subscribe((id) => {
      if (id !== null) {
        this.selectedDevices.set([]);
        this.selectedDevice.set(null);
        apollo
          .watchQuery<
            GetWidgetMetricTypesQuery,
            GetWidgetMetricTypesQueryVariables
          >({
            query: getWidgetMetricTypesQuery,
            variables: {
              categoryId: id,
              widgetType: this.widgetType(),
            },
          })
          .valueChanges.subscribe({
            next: ({ data }) => {
              this.metricTypeOptions.set(data.getWidgetMetricTypes);
              const widgetData = this.widgetData();
              if (widgetData?.metricIds) {
                this.selectedMetrics.set(
                  data.getWidgetMetricTypes.filter((m) =>
                    widgetData.metricIds?.includes(m.id)
                  )
                );
              }
              if (widgetData?.metricTypeId) {
                
                if (
                  data.getWidgetMetricTypes.some(
                    (m) => m.id === widgetData.metricTypeId
                  )
                ) {
                  this.metricType.set(widgetData.metricTypeId);
                }
              }
            },
            error: (err) => {
              
            },
          });
      }
    });

    effect(() => {
      const selectedProps = this.selectedProperties();
      const selectedGroups = this.selectedGroups();
      if (
        (selectedProps.length > 0 || selectedGroups.length > 0) &&
        this.widgetType() === WidgetType.ENERGY_CONSUMPTION
      ) {
        this.getWidgetMetricTypesByEntityIds();
      }
    });
  }
  propertyOrGroupFilterControl = new FormControl('');
  filteredPropertiesOrGroups$: Observable<any[]> | undefined;
  deviceSingleFilterControl = new FormControl('');
  filteredDeviceOptions$: Observable<any[]> | undefined;

  ngOnInit(): void {


    this.filteredPropertiesOrGroups$ = this.propertyOrGroupFilterControl.valueChanges.pipe(
      startWith(''),
      map((search) => {
        const filterValue = (search ?? '').toString().toLowerCase();
        return this.propertiesOrGroups().map(group => ({
          ...group,
          items: group.items.filter((item: any) => item.name.toLowerCase().includes(filterValue))
        })).filter(group => group.items.length > 0);
      })
    );
    this.filteredDeviceOptions$ = combineLatest([
      this.filteredDevicesBase$,
      this.deviceSingleFilterControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([base, search]) => {
        const filterValue = (search ?? '').toString().toLowerCase();
        if (!filterValue) return base;
        return base.filter((d: any) => d.name.toLowerCase().includes(filterValue));
      })
    );

    this.subscriptions.push(
      this.apollo
        .watchQuery<
          CategorySelectOptionsQuery,
          CategorySelectOptionsQueryVariables
        >({
          query: categorySelectOptionsQuery,
          variables: {
            includeGateway: false,
            widgetType: this.widgetType(),
          },
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.categories.set(data.categoryList);
          },
          error: (err) => {
          },
        }),
      this.apollo
        .watchQuery<DeviceListQuery, DeviceListQueryVariables>({
          query: deviceListQuery,
          variables: {
            includeGateway: false,
            widgetType: this.widgetType(),
          },
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.devices.set(data.deviceList);
            const widgetData = this.widgetData();

            if (widgetData?.deviceIds) {
              this.selectedDevices.set(
                data.deviceList.filter((d) =>
                  widgetData.deviceIds?.includes(d.id)
                )
              );
            } else if (widgetData?.deviceId) {
              this.selectedDevice.set(
                data.deviceList.find((d) => d.id === widgetData.deviceId) ??
                  null
              );
            }
          },
          error: (err) => {
          },
        })
    );

    this.criteria.subscribe(() => {
      this.selectedProperties.set([]);
      this.selectedGroups.set([]);
      this.selectedProperty.set(null);
      this.selectedGroup.set(null);
      this.metricType.set('');
      this.metricTypeOptions.set([]);
      if (
        this.displayCriteria() &&
        (this.displayMetric() || this.displayMetrics()) &&
        !this.displayType()
      ) {
        this.selectedProperty.set(null);
        this.selectedGroup.set(null);
        this.selectedDevice.set(null);
        this.selectedPropertyOrGroup.set(null);
        this.categoryId.set(null);
        this.selectedDevice.subscribe((device) => {
          if (device && typeof device !== 'string') {
            this.getWidgetMetricTypesByEntityId(device.id);
          }
        });
        this.selectedProperty.subscribe((property) => {
          if (property && typeof property !== 'string') {
            this.getWidgetMetricTypesByEntityId(property.id);
          }
        });
        this.selectedGroup.subscribe((group) => {
          if (group && typeof group !== 'string') {
            this.getWidgetMetricTypesByEntityId(group.id);
          }
        });
      }
    });
    this.selectedGroup.subscribe((group) => {
      if (this.displayContractOrTariffSelection() && this.displayCriteria()) {
        if (group && typeof group !== 'string') {
          const propertyIdsInGroup = group.properties.map((p) => p.id);
          const filteredContracts = this.allContracts().filter((contract) =>
            contract.devices.some(
              (device) =>
                device.propertyId != null &&
                propertyIdsInGroup.includes(device.propertyId)
            )
          );

          this.contracts.set(filteredContracts);
          const allTariffs = filteredContracts.flatMap((c) => c.tariffs) ?? [];
          this.tariffs.set(allTariffs);

          const selectedContract = this.selectedContract();
          if (
            selectedContract &&
            typeof selectedContract !== 'string' &&
            !filteredContracts.some((c) => c.id === selectedContract.id)
          ) {
            this.selectedContract.set(null);
          }
          const selectedTariff = this.selectedTariff();
          if (
            selectedTariff &&
            typeof selectedTariff !== 'string' &&
            !allTariffs.some((t) => t.id === selectedTariff.id)
          ) {
            this.selectedTariff.set(null);
          }
        } else {
          this.contracts.set(this.allContracts());
          const allTariffs =
            this.allContracts().flatMap((c) => c.tariffs) ?? [];
          this.tariffs.set(allTariffs);
        }
      }
    });

    this.selectedProperty.subscribe((property) => {
      if (this.displayContractOrTariffSelection() && this.displayCriteria()) {
        if (property && typeof property !== 'string') {
          const filteredContracts = this.allContracts().filter((contract) =>
            contract.devices.some(
              (device) =>
                device.propertyId != null && device.propertyId === property.id
            )
          );
          this.contracts.set(filteredContracts);
          const allTariffs = filteredContracts.flatMap((c) => c.tariffs) ?? [];
          this.tariffs.set(allTariffs);
          const selectedContract = this.selectedContract();
          if (
            selectedContract &&
            typeof selectedContract !== 'string' &&
            !filteredContracts.some((c) => c.id === selectedContract.id)
          ) {
            this.selectedContract.set(null);
          }
          const selectedTariff = this.selectedTariff();
          if (
            selectedTariff &&
            typeof selectedTariff !== 'string' &&
            !allTariffs.some((t) => t.id === selectedTariff.id)
          ) {
            this.selectedTariff.set(null);
          }
        } else {
          this.contracts.set(this.allContracts());
          const allTariffs =
            this.allContracts().flatMap((c) => c.tariffs) ?? [];
          this.tariffs.set(allTariffs);
        }
      }
    });
    const editingWidget = this.widgetData();
    if (editingWidget) {
      this.categoryId.set(editingWidget.categoryId ?? null);
      this.display.set(editingWidget.display as WidgetDisplayOption);
      this.yAxis.set((editingWidget.yAxis ?? undefined) as YAxis | undefined);
      this.min.set(editingWidget.min ?? undefined);
      this.max.set(editingWidget.max ?? undefined);
      this.maxTarget.set(editingWidget.targetMax ?? undefined);
      this.minTarget.set(editingWidget.targetMin ?? undefined);
      this.name.set(editingWidget.title);
      this.criteria.set(
        (editingWidget.criteria ?? undefined) as WidgetCriteria | undefined
      );
      this.timeframe.set(
        (editingWidget.timeFrame ?? undefined) as WidgetTimeframe | null
      );
      this.timeframeDate.set(editingWidget.timeFrameDate ?? null);
      this.contractOrTariff.set(
        editingWidget.contractId
          ? 'contract'
          : editingWidget.tariffId
          ? 'tariff'
          : null
      );
      this.selectedColumns.set(
        this.columns().filter((c) =>
          editingWidget.columns?.some((col) => c.id === col)
        )
      );
      this.visualization.set(
        (editingWidget.visualization ?? undefined) as
          | WidgetVisualization
          | undefined
      );
      this.conversionFactor.set(editingWidget.conversionFactor ?? undefined);
      if (
        editingWidget.visualization === WidgetVisualization.SINGLE &&
        editingWidget.targets &&
        editingWidget.type === WidgetType.CO2_EMISSIONS
      ) {
        this.targets.set(
          editingWidget.targets && editingWidget.propertyIds
            ? editingWidget.propertyIds.reduce((properties, pId, i) => {
                properties[pId] = editingWidget.targets?.[i] ?? 0;
                return properties;
              }, {} as Record<string, number>)
            : {}
        );
      } else if (
        editingWidget.visualization === WidgetVisualization.AVERAGE &&
        editingWidget.targets &&
        editingWidget.type === WidgetType.CO2_EMISSIONS
      ) {
        this.targets.set({
          all: editingWidget.targets?.[0] ?? 0,
        });
      } else if (editingWidget.type === WidgetType.ACTUAL_VS_TARGET_VALUES) {
        this.targets.set(
          editingWidget.targets && editingWidget.metricIds
            ? editingWidget.metricIds.reduce((metrics, mId, i) => {
                metrics[mId] = editingWidget.targets?.[i] ?? 0;
                return metrics;
              }, {} as Record<string, number>)
            : {}
        );
        this.thresholds.set(
          editingWidget.thresholds && editingWidget.metricIds
            ? editingWidget.metricIds.reduce((metrics, mId, i) => {
                metrics[mId] = editingWidget.thresholds?.[i] ?? '';
                return metrics;
              }, {} as Record<string, string>)
            : {}
        );
      }
      this.richTextEditorContent.set(editingWidget.customText ?? '');
      this.constant.set(editingWidget.constant ?? undefined);
      this.otherMetricId.set(editingWidget.otherMetricId ?? undefined);
      this.operation.set(editingWidget.operation ?? undefined);
      this.constantOrMetric.set(
        editingWidget.constant
          ? 'constant'
          : editingWidget.otherMetricId
          ? 'metric'
          : undefined
      );
      this.metricType.set(editingWidget.metricTypeId ?? undefined);
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  getSelectedItemsValue(items: Array<{ id: string }>): string {
    return items.map(i => i.id).join(',');
  }

  getSelectedItemValue(item: any): string {
    if (!item) return '';
    if (typeof item === 'object' && item.id) return item.id;
    if (typeof item === 'string') return item;
    return '';
  }

  removeItem(
    selectedList: WritableSignal<Array<{ id: string }>>,
    item: { id: string }
  ) {
    selectedList.update((prev) => prev.filter((a) => a.id !== item.id));
  }
  optionTrackBy(index: number, item: any) {
    return item && item.id ? item.id : index;
  }
  onAdd(
    event: MatChipInputEvent,
    filteredItems: Signal<{ id: string; name: string }[]>,
    selectedItems: WritableSignal<{ id: string }[]>,
    query: ModelSignal<string | undefined>
  ): void {
    if (
      this.widgetType() === WidgetType.ENERGY_CONSUMPTION &&
      selectedItems().length >= 10
    ) {
      event.chipInput?.clear();
      query.set('');
      return;
    }

    const value = (event.value || '').trim().toLowerCase();
    const match = filteredItems().find((a) => a.name.toLowerCase() === value);
    if (match) {
      const previousValue = selectedItems();
      selectedItems.update((prev) => {
        const canAdd =
          this.widgetType() !== WidgetType.ENERGY_CONSUMPTION ||
          prev.length < 10;
        if (!prev.some((a) => a.id === match.id) && canAdd) {
          return [...prev, match];
        }
        return prev;
      });
      if (previousValue === selectedItems()) {
        return;
      }
      event.chipInput?.clear();
      query.set('');
    }
  }
  autocompleteDisplayWith(asset: any) {
    return asset?.name ?? '';
  }

  onSelect(
    event: MatAutocompleteSelectedEvent,
    selectedList: WritableSignal<Array<{ id: string }>>,
    query: any
  ): void {
    const isAlreadySelected = selectedList().some(
      (a) => a.id === event.option.value.id
    );

    selectedList.update((prev) => {
      if (isAlreadySelected) {
        return prev.filter((a) => a.id !== event.option.value.id);
      } else {
        const canAdd =
          this.widgetType() !== WidgetType.ENERGY_CONSUMPTION ||
          prev.length < 10;
        if (canAdd) {
          return [...prev, event.option.value];
        }
      }
      return prev;
    });

    if (query && typeof query.set === 'function') {
      query.set('');
    } else if (typeof query === 'string') {
    }
  }
  onSelectPropertyOrGroup(event: MatAutocompleteSelectedEvent) {
    this.apollo
      .watchQuery<DeviceListQuery, DeviceListQueryVariables>({
        query: deviceListQuery,
        variables: {
          includeGateway: false,
        },
      })
      .valueChanges.subscribe({
        next: ({ data }) => {
          const selectedValue = event.option.value.id;
          const isProperty =
            typeof selectedValue === 'string' && selectedValue.includes('p#');
          this.displayDevices()
            ? this.selectedDevices.set([])
            : this.selectedDevice.set(null);
          this.devices.set(
            data.deviceList.filter((d) =>
              isProperty
                ? d.property.id === event.option.value.id
                : d.groups.some((g) => g.id === event.option.value.id)
            )
          );
        },
        error: (err) => {
        },
      });
  }

  getWidgetMetricTypesByEntityIds() {
    this.apollo
      .watchQuery<
        GetWidgetMetricTypesQuery,
        GetWidgetMetricTypesQueryVariables
      >({
        query: getWidgetMetricTypesQuery,
        variables: {
          categoryId:
            this.widgetType() === WidgetType.KPI_CALCULATION &&
            this.displayDevice()
              ? this.categoryId()
              : null,
          entityIds:
            this.selectedProperties().length > 0
              ? this.selectedProperties().map((p) => p.id)
              : this.selectedGroups().map((g) => g.id),
          widgetType: this.widgetType(),
        },
      })
      .valueChanges.subscribe({
        next: ({ data }) => {
          this.metricTypeOptions.set(data.getWidgetMetricTypes);
          const widgetData = this.widgetData();
          if (widgetData?.metricIds) {
            this.selectedMetrics.set(
              data.getWidgetMetricTypes.filter((m) =>
                widgetData.metricIds?.includes(m.id)
              )
            );
          }
        },
        error: (err) => {
        },
      });
  }

  getWidgetMetricTypesByEntityId(entityId: string) {
    this.apollo
      .watchQuery<
        GetWidgetMetricTypesQuery,
        GetWidgetMetricTypesQueryVariables
      >({
        query: getWidgetMetricTypesQuery,
        variables: {
          categoryId:
            this.widgetType() === WidgetType.KPI_CALCULATION &&
            this.displayDevice()
              ? this.categoryId()
              : null,
          entityId: entityId,
          entityIds:
            this.selectedProperties().length > 0
              ? this.selectedProperties().map((p) => p.id)
              : this.selectedGroups().map((g) => g.id),
          widgetType: this.widgetType(),
        },
      })
      .valueChanges.subscribe({
        next: ({ data }) => {
          this.metricTypeOptions.set(data.getWidgetMetricTypes);
          const widgetData = this.widgetData();
          if (widgetData?.metricIds) {
            this.selectedMetrics.set(
              data.getWidgetMetricTypes.filter((m) =>
                widgetData.metricIds?.includes(m.id)
              )
            );
          }
        },
        error: (err) => {
        },
      });
  }
  onEditorCreated(quill: Quill) {
    const content = this.richTextEditorContent();
    if (!content || content.trim() === '') {
      quill.insertText(0, ' ', { font: 'ABBVoice' });
      quill.deleteText(0, 1);
      quill.setSelection(0, 0);
      quill.format('font', 'ABBVoice');
    } 
    (quill.getModule('toolbar') as any).addHandler('image', () => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');

      input.addEventListener('mousedown', (e) => e.stopPropagation());
      input.addEventListener('focus', (e) => e.stopPropagation());

      input.click();

      input.onchange = () => {
        const file = input.files?.[0];
        const maxSize = 200 * 1024;

        if (file) {
          if (!file.type.startsWith('image/')) {
            this.toast.notifyError('Only image files are allowed.');
            return;
          }
          if (file.size > maxSize) {
            this.toast.notifyError(
              'File size exceeds the maximum limit of 200 KB.'
            );
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result;
            const range = quill.getSelection();
            if (range) {
              quill.insertEmbed(range.index, 'image', base64);

              setTimeout(() => {
                const html = quill.root.innerHTML;
                this.richTextEditorContent.set(html);
              });
            }
          };
          reader.readAsDataURL(file);
        }
      };
    });
  }

  onDateChange(selectedDate: Date | null) {
    if (selectedDate) {
      const midnightUTC = new Date(
        Date.UTC(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          0,
          0,
          0,
          0
        )
      );
      this.timeframeDate.set(midnightUTC);

    } else {
      this.timeframeDate.set(null);
    }
  }

  async onSubmit(form: NgForm) {
    
    if (form.invalid) {
      this.toast.notifyWarning(
        t(this.prefix, `Please fill all required fields`, this.translate)
      );
      return;
    }
    if (this.displayOptions().length === 1) {
      this.display.set(this.displayOptions()[0].id);
    }
    const display = this.display();
    if (!display) {
      console.warn('display missing');
      return;
    }
    const propertyOrGroup = this.selectedPropertyOrGroup();
    const property = this.selectedProperty();
    const group = this.selectedGroup();
    const device = this.selectedDevice();
    const tariff = this.selectedTariff();
    const contract = this.selectedContract();
    const sharedVariables: Omit<
      UpdateWidgetMutationVariables,
      'id' | 'dashboardId'
    > = {
      type: this.widgetType(),
      metricTypeId: this.displayMetric() ? this.metricType() : undefined,
      categoryId: this.categoryId(),
      deviceIds: this.displayDevices()
        ? this.selectedDevices().map((d) => d.id)
        : undefined,
      display,
      title: this.name() ?? '',
      groupId: this.displayGroup()
        ? group && typeof group === 'object'
          ? group.id
          : null
        : this.displayPropertyOrGroup() &&
          propertyOrGroup &&
          typeof propertyOrGroup === 'object' &&
          propertyOrGroup.__typename === 'Group'
        ? propertyOrGroup.id
        : null,
      propertyId: this.displayProperty()
        ? property && typeof property === 'object'
          ? property.id
          : null
        : this.displayPropertyOrGroup() &&
          propertyOrGroup &&
          typeof propertyOrGroup === 'object' &&
          propertyOrGroup.__typename === 'Property'
        ? propertyOrGroup.id
        : null,
      max: this.displayMinMax() ? this.max() : undefined,
      min: this.displayMinMax() ? this.min() : undefined,
      targetMax: this.displayTargetValues() ? this.maxTarget() : undefined,
      targetMin: this.displayTargetValues() ? this.minTarget() : undefined,
      yAxis: this.displayHasYAxis() ? this.yAxis() : undefined,
      criteria: this.displayCriteria() ? this.criteria() : undefined,
      groupIds: this.displayGroups()
        ? this.selectedGroups().map((g) => g.id)
        : undefined,
      propertyIds: this.displayProperties()
        ? this.selectedProperties().map((p) => p.id)
        : undefined,
      timeFrame: this.displayTimeframe() ? this.timeframe() : undefined,
      timeFrameDate: this.displayTimeframeDate()
        ? this.timeframeDate()
        : undefined,
      deviceId:
        this.displayDevice() && device && typeof device === 'object'
          ? device.id
          : undefined,
      tariffId:
        this.displayTariff() && tariff && typeof tariff === 'object'
          ? tariff.id
          : undefined,
      contractId:
        this.displayContract() && contract && typeof contract === 'object'
          ? contract.id
          : undefined,
      columns: this.displayColumns()
        ? this.selectedColumns().map((c) => c.id)
        : undefined,
      conversionFactor: this.displayConversionFactor()
        ? this.conversionFactor()
        : undefined,
      visualization: this.displayVisualizationMode()
        ? this.visualization()
        : undefined,
      targets:
        this.displayTargetList() || this.displayTargetsWithThresholds()
          ? this.targetListElements().map((el) => this.targets()[el.id] ?? 0)
          : undefined,
      customText: this.displayTextEditor()
        ? this.richTextEditorContent()
        : undefined,
      thresholds: this.displayTargetsWithThresholds()
        ? this.targetListElements().map((el) => this.thresholds()[el.id] ?? '')
        : undefined,
      metricIds: this.displayMetrics()
        ? this.selectedMetrics().map((m) => m.id)
        : undefined,
      constant: this.displayConstant() ? this.constant() : undefined,
      otherMetricId: this.displayOtherMetricId()
        ? this.otherMetricId()
        : undefined,
      operation: this.displayOperations() ? this.operation() : undefined,
    };
    this.abbSubmit.emit({ sharedVariables, display });
  }

  preventTyping(event: KeyboardEvent) {
    const allowed = [
      'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Escape', 'Enter'
    ];
    if (
      event.key.length === 1 &&
      !allowed.includes(event.key)
    ) {
      event.preventDefault();
    }
  }
}
